import Button from '@components/_atoms/Button';
import BasicModal from '@components/_atoms/Modal/Basic';
import { Toast } from '@components/_atoms/Toast';
import Typography from '@components/_atoms/Typography';
import { ButtonColor, TypoVariant } from '@constants/atoms';
import styled from '@emotion/styled';
import useGetUserInterestInfoCategory from '@hooks/fetch/spring/user-interest-info-category/useGetUserInterestInfoCategory';
import useSaveUserInterestInfo from '@hooks/fetch/spring/user-interest-info/useSaveUserInterestInfo';
import useGetUserInterestInfo from '@hooks/fetch/spring/user-interest-info/useUserInterestInfo';
import { blue } from '@styles/Colors';
import { useTranslation } from 'next-i18next';
import { useEffect, useMemo, useState } from 'react';

const CategoryListWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
`;

const CategoryWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 8px;
  align-self: stretch;
`;

const CategoryTitleWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  align-self: stretch;
`;

const CategoryCountWrapper = styled.span`
  display: flex;
  padding: 1px 6px;
  justify-content: center;
  align-items: center;
  gap: 4px;

  border-radius: 4px;
  background: var(--blue-scale-blue-1, ${blue.blue1});
`;

const CategoryItemWrapper = styled.div`
  display: flex;
  max-width: 640px;
  align-items: flex-start;
  align-content: flex-start;
  gap: 8px;
  align-self: stretch;
  flex-wrap: wrap;
`;

type Props = {
  isOpen: boolean;
  onClose: () => void;
};

export default function InterestInfoModal({ isOpen, onClose }: Props) {
  const { data: categoryData } = useGetUserInterestInfoCategory();
  const { data: userInterestInfoData } = useGetUserInterestInfo();
  const { i18n, t } = useTranslation('interest');

  const ko = i18n.language === 'ko';
  const nameKey = ko ? 'name' : 'nameEn';

  const [categoryItemIdList, setCategoryItemIdList] = useState<number[]>([]);
  const { mutateAsync } = useSaveUserInterestInfo();

  const handleOk = async () => {
    const response = await mutateAsync({
      categoryItemIdList,
    });

    if (response.success) {
      Toast.success(t('modal.save-success-message'));
      onClose();
    } else {
      Toast.error(response.message.text);
    }
  };

  const originalCategoryItemIdList: number[] = useMemo(
    () =>
      userInterestInfoData?.data.reduce<number[]>(
        (result, item) => [...result, item.userInterestInfoCategoryItemId],
        [],
      ) || [],
    [userInterestInfoData],
  );

  useEffect(() => {
    setCategoryItemIdList([...originalCategoryItemIdList]);
  }, [originalCategoryItemIdList]);

  return (
    <>
      <BasicModal
        isOpen={isOpen}
        width={720}
        padding={40}
        title={t('modal.title')}
        subTitle={t('modal.content')}
        okText={t('modal.complete-button')}
        cancelText={t('later-button')}
        disabled={!categoryItemIdList.length}
        ok={handleOk}
        onClose={() => {
          setCategoryItemIdList([...originalCategoryItemIdList]);
          onClose();
        }}>
        <CategoryListWrapper>
          {categoryData?.data.map((category) => (
            <CategoryWrapper key={category.id}>
              <CategoryTitleWrapper>
                <Typography variant={TypoVariant.SH3}>{category[nameKey]}</Typography>
                <CategoryCountWrapper>
                  <Typography variant={TypoVariant.SH3} color={blue.blue3}>
                    {category.categoryItemList.filter((item) => categoryItemIdList.includes(item.id)).length}
                  </Typography>
                </CategoryCountWrapper>
              </CategoryTitleWrapper>
              <CategoryItemWrapper>
                {category.categoryItemList.map((item) => (
                  <Button
                    key={item.id}
                    text={item[nameKey]}
                    color={categoryItemIdList.includes(item.id) ? ButtonColor.OutlinedBlue : ButtonColor.OutlinedGray}
                    onClick={() => {
                      setCategoryItemIdList((prev) =>
                        prev.includes(item.id) ? prev.filter((id) => id !== item.id) : [...prev, item.id],
                      );
                    }}
                  />
                ))}
              </CategoryItemWrapper>
            </CategoryWrapper>
          ))}
        </CategoryListWrapper>
      </BasicModal>
    </>
  );
}
